import { z } from 'zod';
import { TeamRole } from '../../../types/team.util';

/**
 * Why an in-care group has a destination guide.
 * `selected_team_member` is an admin pick, not the automatic ladder.
 */
export const GuideDeletionDestinationReasonSchema = z.enum([
  'manager',
  'team_supervisor',
  'selected_team_member',
]);

export type GuideDeletionDestinationReason = z.infer<
  typeof GuideDeletionDestinationReasonSchema
>;

const guideDeletionCandidateSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  role: z.nativeEnum(TeamRole),
});

const guideDeletionDestinationGroupSchema = z.object({
  teamId: z.number().int(),
  teamName: z.string(),
  memberCount: z.number().int().nonnegative(),
  destinationUserId: z.string().uuid(),
  destinationName: z.string(),
  reason: GuideDeletionDestinationReasonSchema,
});

const guideDeletionOverrideGroupSchema = z.object({
  teamId: z.number().int(),
  teamName: z.string(),
  memberCount: z.number().int().nonnegative(),
  candidates: z.array(guideDeletionCandidateSchema),
});

/**
 * Read-only preview of what deleting a guide would do to their assigned caseload.
 * `blocked` is true when in-care members have no team, or a team with nobody
 * else who can take them. Groups that still need a pick are `overrideGroups`
 * and do not set `blocked` by themselves.
 */
export const GuideDeletionPreviewDtoSchema = z.object({
  blocked: z.boolean(),
  userMessage: z.string().nullable(),
  outreachNotYetContactedCount: z.number().int().nonnegative(),
  independenceCount: z.number().int().nonnegative(),
  inCareMoveCount: z.number().int().nonnegative(),
  destinationGroups: z.array(guideDeletionDestinationGroupSchema),
  overrideGroups: z.array(guideDeletionOverrideGroupSchema),
});

export type GuideDeletionPreviewDto = z.infer<
  typeof GuideDeletionPreviewDtoSchema
>;

/**
 * Optional per-team picks for a single-guide delete. Omitted picks are
 * recomputed from the ladder; a pick is accepted only when that person is
 * still a candidate for that team.
 */
export const DeleteGuideRequestDtoSchema = z.object({
  overrides: z
    .array(
      z.object({
        teamId: z.number().int(),
        userId: z.string().uuid(),
      })
    )
    .optional(),
});

export type DeleteGuideRequestDto = z.infer<typeof DeleteGuideRequestDtoSchema>;
